import { useState, useEffect, useCallback, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { $getSelection, $isRangeSelection, $getRoot } from 'lexical';
import {
  CONDITION_PARAMS, extractParamValue, evalCondition,
  getTagIdByNimi,
} from '../utils/valmislauseStorage';
import {
  getValmislauseet, createValmislause, updateValmislause, deleteValmislause,
  reorderValmislauseet,
} from '../api/api';
import MinimalLexicalEditor from './MinimalLexicalEditor';
import TagReportTypesPicker from './TagReportTypesPicker';
import ChapterScopePicker, { useReportChaptersMap } from './ChapterScopePicker';
import { ConditionFields, renderWithMuuttujat, sanitizeTagName } from './ValmislauseFields';
import { confirmAndRun } from './utils/ConfirmAndRun.jsx';

const UNIVERSAL = '__universal__';

// Small deterministic palette for the per-tag type dots — assigned by each
// type's position in allReportTypes (stable for a session; report types
// have no fixed enum/color of their own).
const TYPE_DOT_COLORS = ['#2F6FED', '#7C5CFC', '#0D9C94', '#D97706', '#C0392B', '#1F7A4D', '#B0577A'];
const UNIVERSAL_DOT_COLOR = '#AEB6C2';
function typeDotColor(code, allReportTypes) {
  const idx = allReportTypes.findIndex(r => r.type === code);
  return idx === -1 ? UNIVERSAL_DOT_COLOR : TYPE_DOT_COLORS[idx % TYPE_DOT_COLORS.length];
}

/* ============================================================
   ValmislauseCard — one sentence. Condition display/edit, title/text
   edit-in-place, reorder and delete are always available; clicking the
   card body to insert (and the per-card "Käytä" affordance that implies)
   only exists when `onUse` is given — there's nothing to insert into on
   the admin page, only inside a report's text block.
   ============================================================ */
function ValmislauseCard({ item, isFirst, isLast, rakennusData, mergedRakennusData, onSave, onDelete, onMoveUp, onMoveDown, onUse }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editText, setEditText] = useState(item.teksti);
  const [editingCondition, setEditingCondition] = useState(false);
  const [condParam, setCondParam] = useState('rakennusvuosi');
  const [condOp, setCondOp] = useState('<');
  const [condVal, setCondVal] = useState('');

  const condResult = item.condition ? evalCondition(item.condition, rakennusData, mergedRakennusData) : null;
  const isMatch = condResult === true;
  const currentVal = item.condition ? extractParamValue(item.condition.param, rakennusData, mergedRakennusData) : null;
  const paramDef = item.condition ? CONDITION_PARAMS.find(d => d.key === item.condition.param) : null;
  const hasCond = !!item.condition;

  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const saveEdit = () => {
    onSave({ title: editTitle.trim(), teksti: editText.trim() });
    setEditing(false);
  };
  const cancelEdit = () => {
    setEditTitle(item.title);
    setEditText(item.teksti);
    setEditing(false);
  };

  const openConditionEditor = () => {
    setEditingCondition(v => !v);
    const p = item.condition?.param ?? 'rakennusvuosi';
    setCondParam(p);
    const def = CONDITION_PARAMS.find(d => d.key === p);
    setCondOp(item.condition?.operator ?? (def?.type === 'string' ? '=' : '<'));
    setCondVal(item.condition?.value?.toString() ?? '');
  };
  const handleCondParamChange = (p) => {
    setCondParam(p);
    const def = CONDITION_PARAMS.find(d => d.key === p);
    setCondOp(def?.type === 'string' ? '=' : '<');
  };
  const saveCondition = () => {
    if (!condVal.toString().trim()) return;
    onSave({ condition: { param: condParam, operator: condOp, value: condVal } });
    setEditingCondition(false);
  };
  const removeCondition = () => {
    onSave({ condition: null });
    setEditingCondition(false);
  };

  return (
    <div className="rounded border overflow-hidden" onClick={onUse ? () => onUse(item) : undefined}
      style={{
        borderColor: isMatch || !hasCond ? '#b8ddb8' : '#dee2e6',
        cursor: onUse ? 'pointer' : 'default',
      }}
      onMouseEnter={onUse ? e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.08)'; } : undefined}
      onMouseLeave={onUse ? e => { e.currentTarget.style.boxShadow = 'none'; } : undefined}
    >
      <div className="d-flex align-items-center gap-2" style={{
        padding: '7px 12px', fontSize: '0.82rem', fontWeight: 650,
        background: isMatch || !hasCond ? '#eafbf1' : '#f8f9fa',
        color: isMatch || !hasCond ? '#198754' : '#6c757d',
      }}>
        {hasCond ? (
          <>JOS <span style={{ fontFamily: 'ui-monospace,monospace', color: '#212529' }}>{paramDef?.label ?? item.condition.param} {item.condition.operator} {item.condition.value}</span></>
        ) : (
          <button onClick={stop(openConditionEditor)} style={{
            border: '1px dashed #adb5bd', background: '#fff', color: '#6c757d', cursor: 'pointer',
            borderRadius: 999, padding: '2px 9px', fontSize: '0.78rem', fontWeight: 700,
          }}>
            + ehto
          </button>
        )}
        <div className="d-flex align-items-center gap-1" style={{ marginLeft: 'auto' }}>
          {hasCond && (
            <span style={{
              fontSize: '0.74rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999,
              background: isMatch ? '#198754' : '#fff', color: isMatch ? '#fff' : '#6c757d',
              border: isMatch ? 'none' : '1px solid #dee2e6',
            }}>
              {currentVal != null ? `nyt ${currentVal}` : 'ehdollinen'}
            </span>
          )}
          <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.78rem', padding: '1px 7px' }}
            onClick={stop(onMoveUp)} disabled={isFirst} title="Siirrä ylös">↑</button>
          <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.78rem', padding: '1px 7px' }}
            onClick={stop(onMoveDown)} disabled={isLast} title="Siirrä alas">↓</button>
          {hasCond && (
            <button onClick={stop(openConditionEditor)} title="Muokkaa ehtoa" style={{
              border: '1px solid #dee2e6', background: '#fff', color: '#198754', cursor: 'pointer',
              borderRadius: 6, padding: '2px 6px', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1,
            }}>
              ⚙
            </button>
          )}
          <button onClick={stop(() => setEditing(v => !v))} style={{
            border: '1px solid #dee2e6', background: editing ? '#198754' : '#fff', color: editing ? '#fff' : '#6c757d',
            cursor: 'pointer', borderRadius: 6, padding: '2px 9px', fontSize: '0.78rem', fontWeight: 700,
          }}>
            {editing ? 'Muokataan' : 'Muokkaa'}
          </button>
          <button onClick={stop(onDelete)} title="Poista valmislause" style={{
            border: 'none', background: 'none', cursor: 'pointer', padding: '2px 5px',
            display: 'flex', alignItems: 'center', opacity: .7,
            color: isMatch || !hasCond ? '#198754' : '#6c757d',
          }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {editingCondition && (
        <div onClick={e => e.stopPropagation()} style={{ borderBottom: '1px solid #dee2e6', padding: '9px 12px', background: '#f8f9fa' }}>
          <ConditionFields
            param={condParam} op={condOp} val={condVal}
            onParamChange={handleCondParamChange} onOpChange={setCondOp} onValChange={setCondVal}
            rakennusData={rakennusData} mergedRakennusData={mergedRakennusData}
          />
          <div className="d-flex gap-2 mt-2">
            <button className="btn btn-sm btn-success" style={{ fontSize: '0.82rem', padding: '2px 9px' }} onClick={saveCondition}>Tallenna</button>
            {hasCond && (
              <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.82rem', padding: '2px 9px' }} onClick={removeCondition}>Poista ehto</button>
            )}
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.82rem', padding: '2px 9px' }} onClick={() => setEditingCondition(false)}>Peru</button>
          </div>
        </div>
      )}

      <div onClick={editing ? (e => e.stopPropagation()) : undefined} style={{ padding: '10px 12px' }}>
        {editing ? (
          <>
            <div className="mb-2">
              <MinimalLexicalEditor value={editText} onChange={setEditText} placeholder="Valmislauseteksti…" minHeight={80} />
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-success" style={{ fontSize: '0.84rem' }} onClick={saveEdit}>Tallenna</button>
              <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.84rem' }} onClick={cancelEdit}>Peru</button>
            </div>
          </>
        ) : (
          <>
            {item.title && (
              <div style={{ fontSize: '0.94rem', fontWeight: 650, marginBottom: 4 }}>{renderWithMuuttujat(item.title)}</div>
            )}
            <div style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5 }}>{renderWithMuuttujat(item.teksti)}</div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ValmislauseBrowser — tag rail + type-tab row + sentence list +
   muuttujat panel. The one shared implementation behind both the report
   editor's insert modal (ValmislauseModal, which wraps this in a
   backdrop) and the /raportit?tab=valmislauseet admin page (which
   renders this directly as the page body, no wrapper at all).

   Two things change behavior, purely from which props are passed:
   - `typeTabRow`: 'single' shows today's two-tab "Tämä raportti (TYPE)" /
     "Yleiset" (needs `reportType`); 'all' shows every report type as its
     own pill with counts, plus an overflow dropdown (no report context
     needed — this is the admin page's view across the whole library).
   - `tags` / `onAddTag` / `onRemoveTag` / `onInsert`: only meaningful
     when browsing from inside a report's text block. Left undefined on
     the admin page, which hides the per-card "Käytä" affordance and the
     "Käytä valmislausetta" footer bar rather than rendering them inert.
   `rakennusData`/`mergedRakennusData` are similarly optional — without
   them, condition evaluation and the muuttujat panel just show no live
   values instead of crashing (CONDITION_PARAMS/extractParamValue already
   tolerate undefined input).
   ============================================================ */

// One row in the tag rail — pulled out so the "Tämä kappale" grouping can
// render it twice (once per group) without duplicating the JSX.
function TagRow({ t, selectedTag, setSelectedTag, handleDeleteTag, allReportTypes }) {
  return (
    <div className="d-flex align-items-center" style={{
      borderRadius: 7, marginBottom: 2,
      background: selectedTag === t.nimi ? '#eafbf1' : 'transparent',
    }}>
      <button onClick={() => setSelectedTag(t.nimi)}
        className="d-flex align-items-center gap-2 text-start flex-grow-1"
        style={{
          border: 'none', background: 'none', borderRadius: 7, padding: '7px 9px', fontSize: '0.88rem', fontWeight: 600,
          color: selectedTag === t.nimi ? '#198754' : '#212529', minWidth: 0,
        }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
          background: selectedTag === t.nimi ? '#198754' : (t.report_types?.length ? typeDotColor(t.report_types[0], allReportTypes) : UNIVERSAL_DOT_COLOR),
        }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.nimi}</span>
      </button>
      <button onClick={e => handleDeleteTag(t, e)} title="Poista tagi" aria-label={`Poista tagi ${t.nimi}`}
        style={{ border: 'none', background: 'none', color: '#adb5bd', fontSize: '0.82rem', padding: '4px 7px', flexShrink: 0, cursor: 'pointer' }}>
        ×
      </button>
    </div>
  );
}

// New-tag modal, opened either from the rail's own "+ Uusi tagi" footer
// button (generic) or from a chapter's own "+" (pre-scoped to that chapter)
// — replaces the old always-visible inline input+button, which crowded the
// rail and gave no room to show what the new tag would be scoped to.
// One tag chip in the center panel's tag row (chapters-nav mode) — same
// select/delete actions as TagRow, just styled as a chip instead of a rail
// row, since tags now live next to the chapter they belong to rather than
// nested under it in the rail.
function TagChip({ t, selectedTag, setSelectedTag, handleDeleteTag }) {
  const isSelected = selectedTag === t.nimi;
  const restBg = isSelected ? '#198754' : '#eafbf1';
  const hoverBg = isSelected ? '#15723f' : '#dcf5e4';
  return (
    <span className="d-inline-flex align-items-center" style={{
      gap: 6, borderRadius: 999, padding: '4px 6px 4px 13px', fontSize: '0.82rem', fontWeight: 600,
      background: restBg,
      color: isSelected ? '#fff' : '#198754',
      border: `1px solid ${isSelected ? '#198754' : '#b8ddb8'}`,
      cursor: 'pointer',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.background = restBg; }}
      onClick={() => setSelectedTag(t.nimi)}
    >
      <button type="button" onClick={e => { e.stopPropagation(); setSelectedTag(t.nimi); }}
        style={{ border: 'none', background: 'none', color: 'inherit', font: 'inherit', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
        {t.nimi}
      </button>
      <button type="button" onClick={e => { e.stopPropagation(); handleDeleteTag(t, e); }} title="Poista tagi" aria-label={`Poista tagi ${t.nimi}`}
        style={{ border: 'none', background: 'none', color: 'inherit', opacity: 0.75, cursor: 'pointer', fontSize: '0.78rem', lineHeight: 1, padding: '3px' }}
        onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = 0.75; }}>
        ×
      </button>
    </span>
  );
}

function NewTagModal({ reportTypeLabel, sectionLabel, onCreate, onClose }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try { await onCreate(name); } finally { setSaving(false); }
  };

  return (
    <div
      // Above ValmislauseModal's own backdrop (1000, see below) so it can
      // open nested inside it, still below Bootstrap's real modal/confirm
      // dialogs (1050/1055).
      style={{ position: 'fixed', inset: 0, background: 'rgba(33,37,41,0.45)', zIndex: 1010, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} className="shadow-lg" style={{ width: 360, background: '#fff', borderRadius: 12, padding: '18px 20px' }}>
        <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 10 }}>Uusi tagi</div>
        {(reportTypeLabel || sectionLabel) && (
          <div className="d-flex flex-wrap gap-1 mb-2">
            {reportTypeLabel && (
              <span style={{ fontSize: '0.74rem', padding: '2px 9px', borderRadius: 999, background: '#eef1f4', color: '#3e4c59' }}>
                {reportTypeLabel}
              </span>
            )}
            {sectionLabel && (
              <span style={{ fontSize: '0.74rem', padding: '2px 9px', borderRadius: 999, background: '#eafbf1', color: '#198754' }}>
                Kappale: {sectionLabel}
              </span>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          className="form-control form-control-sm"
          placeholder="Tagin nimi"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') onClose(); }}
          style={{ fontSize: '0.85rem' }}
        />
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={saving}>Peruuta</button>
          <button type="button" className="btn btn-sm btn-success" disabled={!name.trim() || saving} onClick={submit}>
            {saving ? 'Luodaan…' : '+ Luo'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ValmislauseBrowser({
  allTags, allReportTypes,
  typeTabRow = 'single',
  reportType,
  tags, onAddTag, onRemoveTag, onInsert,
  onCreateTag, onDeleteTag, onTagReportTypesChange, onTagSectionsChange,
  sectionType,
  rakennusData, mergedRakennusData,
  // Initial values + change callbacks for the admin page's URL-synced view
  // state (which type tab, which tag) — optional, so the report-editor
  // modal (which has no URL of its own to sync) can keep using plain
  // internal state by just not passing these.
  initialTypeFilter, onTypeFilterChange,
  initialSelectedTag, onSelectedTagChange,
  initialActiveChapter, onActiveChapterChange,
  // Called after a card's "use" click or the "Käytä valmislausetta"
  // footer button successfully links/inserts — the modal wrapper uses
  // this to close itself; the admin page has no reason to pass it.
  onDone,
}) {
  const linkedTags = tags || [];
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilterState] = useState(() => initialTypeFilter ?? (typeTabRow === 'all' ? 'ALL' : (reportType || UNIVERSAL)));
  const setTypeFilter = (code) => { setTypeFilterState(code); onTypeFilterChange?.(code); };
  // Open/closed state for the "Raporttityyppi" dropdown at the top — a
  // single-select trigger+panel, same pattern as TagReportTypesPicker,
  // replacing the old pill row (which needed an overflow "+ Muut…" select
  // once there were more report types than fit).
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [selectedTag, setSelectedTagState] = useState(initialSelectedTag ?? linkedTags[0] ?? null);
  const setSelectedTag = (nimi) => { setSelectedTagState(nimi); onSelectedTagChange?.(nimi); };
  // Which chapter is open in the rail — always exactly one (a plain nav
  // list, not an accordion): starts on the current chapter when known
  // (report-editor modal) or the URL/localStorage-synced value (admin
  // page), seeded properly once chapterDefs loads (see currentChapterKey
  // below).
  const [activeChapterState, setActiveChapterState] = useState(initialActiveChapter ?? sectionType ?? null);
  const setActiveChapter = (key) => { setActiveChapterState(key); onActiveChapterChange?.(key); };
  const [list, setList] = useState([]);
  // Open "new tag" modal state — null when closed; { sectionKey, sectionLabel }
  // (sectionLabel for display only) when opened from a chapter's own "+", so
  // the created tag starts scoped to that chapter instead of needing a
  // second trip through ChapterScopePicker.
  const [newTagModal, setNewTagModal] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTeksti, setNewTeksti] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const [newHasCondition, setNewHasCondition] = useState(false);
  const [newCondParam, setNewCondParam] = useState('rakennusvuosi');
  const [newCondOp, setNewCondOp] = useState('<');
  const [newCondVal, setNewCondVal] = useState('');
  const newTekstiEditorRef = useRef(null);

  const selectedTagObj = selectedTag ? allTags.find(t => t.nimi === selectedTag) : null;
  // Prefer this component's own fresh `allTags` over the module-level name
  // cache (getTagIdByNimi) — the cache is a separate snapshot written by
  // whichever component last fetched tags (could be a report editor tab,
  // stale by the time this page's own allTags has loaded) and was
  // previously checked FIRST, so a stale/wrong id could silently override
  // the correct one: the rail (name-matched against allTags) would show the
  // right tag selected while the sentence list loaded a different tag's
  // (often empty) valmislauseet. Only fall back to the cache while allTags
  // itself hasn't loaded this tag yet.
  const selectedTagId = selectedTag ? (selectedTagObj?.id ?? getTagIdByNimi(selectedTag) ?? null) : null;

  const handleNewCondParamChange = (p) => {
    setNewCondParam(p);
    const def = CONDITION_PARAMS.find(d => d.key === p);
    setNewCondOp(def?.type === 'string' ? '=' : '<');
  };

  // Inserts {{key}} at the editor's last cursor position rather than always
  // appending at the end. editor.focus() restores the last known selection
  // even after the editor lost focus (clicking a muuttuja row blurs it).
  const insertMuuttuja = (key) => {
    const token = `{{${key}}}`;
    const editor = newTekstiEditorRef.current;
    if (!editor) { setNewTeksti(prev => prev + token); return; }
    editor.focus(() => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) selection.insertText(token);
        else $getRoot().selectEnd().insertText(token);
      });
    });
  };

  const loadList = useCallback((tagId) => {
    if (!tagId) { setList([]); return; }
    getValmislauseet(tagId).then(r => setList(r.data ?? [])).catch(() => setList([]));
  }, []);

  const resetNewForm = () => {
    setAddingNew(false);
    setNewTitle('');
    setNewTeksti('');
    setNewHasCondition(false);
    setNewCondParam('rakennusvuosi');
    setNewCondOp('<');
    setNewCondVal('');
  };

  useEffect(() => {
    resetNewForm();
    loadList(selectedTagId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagId, loadList]);

  const handleCreateValmislause = async () => {
    if (!selectedTagId || !newTeksti.trim()) return;
    const def = newHasCondition ? CONDITION_PARAMS.find(d => d.key === newCondParam) : null;
    if (newHasCondition) {
      if (!newCondVal.trim()) return;
      if (def?.type === 'number' && isNaN(Number(newCondVal))) return;
    }
    const condition = newHasCondition
      ? { param: newCondParam, operator: newCondOp, value: def?.type === 'number' ? Number(newCondVal) : newCondVal }
      : null;
    setSavingNew(true);
    try {
      await createValmislause(selectedTagId, {
        title: newTitle.trim(),
        teksti: newTeksti.trim(),
        condition,
        jarjestys: list.length,
      });
      window.dispatchEvent(new Event('valmislause-updated'));
      resetNewForm();
      loadList(selectedTagId);
    } finally {
      setSavingNew(false);
    }
  };

  const handleSaveItem = async (item, patch) => {
    await updateValmislause(item.id, patch);
    setList(prev => prev.map(t => t.id === item.id ? { ...t, ...patch } : t));
    window.dispatchEvent(new Event('valmislause-updated'));
  };

  const handleDeleteValmislause = (t) => {
    confirmAndRun({
      title: 'Poista valmislause',
      message: `Poistetaanko valmislause "${t.title || t.teksti.slice(0, 60)}"?`,
      confirmText: 'Poista',
      action: async () => {
        await deleteValmislause(t.id);
        setList(prev => prev.filter(x => x.id !== t.id));
        window.dispatchEvent(new Event('valmislause-updated'));
      },
    }).catch(() => {});
  };

  const handleMove = async (id, direction) => {
    const idx = list.findIndex(t => t.id === id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return;
    const newList = [...list];
    [newList[idx], newList[swapIdx]] = [newList[swapIdx], newList[idx]];
    setList(newList);
    await reorderValmislauseet(selectedTagId, newList.map(t => t.id));
    window.dispatchEvent(new Event('valmislause-updated'));
  };

  const handleUse = onInsert ? (item) => {
    if (selectedTag && !linkedTags.includes(selectedTag)) onAddTag?.(selectedTag);
    onInsert(item.teksti);
    onDone?.();
  } : undefined;

  // Links the selected tag to this block without inserting any specific
  // sentence's text — just "this field is about ASBESTI".
  const handleUseTag = () => {
    if (selectedTag && !linkedTags.includes(selectedTag)) onAddTag?.(selectedTag);
    onDone?.();
  };

  const handleDeleteTag = (t, e) => {
    e.stopPropagation();
    confirmAndRun({
      title: 'Poista tagi',
      message: `Poistetaanko tagi "${t.nimi}"? Tämä poistaa myös sen kaikki valmislauseet.`,
      confirmText: 'Poista',
      action: async () => {
        await onDeleteTag(t.id);
        if (linkedTags.includes(t.nimi)) onRemoveTag?.(t.nimi);
        if (selectedTag === t.nimi) setSelectedTag(null);
      },
    }).catch(() => {});
  };

  const handleCreateTagModal = async (rawName) => {
    const name = sanitizeTagName(rawName);
    if (!name) return;
    const scopeType = typeFilter === UNIVERSAL || typeFilter === 'ALL' ? [] : [typeFilter];
    const sectionKey = newTagModal?.sectionKey;
    const sections = sectionKey && scopeType.length ? [{ report_type: scopeType[0], section_type: sectionKey }] : [];
    const created = await onCreateTag(name, scopeType, sections);
    if (created) {
      setSelectedTag(created.nimi);
      if (sectionKey) setActiveChapter(sectionKey);
    }
    setNewTagModal(null);
  };

  const currentReportTypeDef = allReportTypes.find(r => r.type === reportType);
  const typeChips = [
    ...(currentReportTypeDef ? [{ code: reportType, label: `Tämä raportti (${currentReportTypeDef.name})` }] : []),
    { code: UNIVERSAL, label: 'Yleiset' },
  ];

  const typeTabLabel = (key) => {
    if (key === 'ALL') return 'Kaikki';
    if (key === UNIVERSAL) return 'Yleinen';
    return allReportTypes.find(r => r.type === key)?.name || key;
  };
  const typeTabCount = (key) => allTags.filter(t => {
    if (key === 'ALL') return true;
    if (key === UNIVERSAL) return !t.report_types?.length;
    return t.report_types?.includes(key);
  }).length;

  // Options for the single "Raporttityyppi" dropdown — in 'single' mode
  // just the two chips this report/tag could be scoped to; in 'all' mode
  // every report type, since a dropdown has no pill-row width limit to
  // work around (no more overflow "+ Muut…" needed).
  const typeOptions = typeTabRow === 'single'
    ? typeChips.map(c => ({ key: c.code, label: c.label, dot: c.code === UNIVERSAL ? UNIVERSAL_DOT_COLOR : typeDotColor(c.code, allReportTypes), count: null }))
    : [
        { key: 'ALL', label: 'Kaikki', dot: '#495057', count: typeTabCount('ALL') },
        { key: UNIVERSAL, label: 'Yleinen', dot: UNIVERSAL_DOT_COLOR, count: typeTabCount(UNIVERSAL) },
        ...allReportTypes.map(r => ({ key: r.type, label: r.name, dot: typeDotColor(r.type, allReportTypes), count: typeTabCount(r.type) })),
      ];
  const activeTypeOption = typeOptions.find(o => o.key === typeFilter) ?? typeOptions[0];

  const visibleTags = allTags
    .filter(t => !query.trim() || t.nimi.toLowerCase().includes(query.trim().toLowerCase()))
    .filter(t => {
      if (typeFilter === 'ALL') return true;
      if (typeFilter === UNIVERSAL) return !t.report_types?.length;
      return t.report_types?.includes(typeFilter);
    });

  // Chapters become the rail's primary structure whenever exactly one
  // report type is in scope — the single-report modal's own `reportType`,
  // or (in the admin's 'all' mode) a specific type tab, not 'Kaikki'/
  // 'Yleinen'. A tag with no chapter entries for that report type isn't
  // restricted to one, so it sits in the always-visible "Yleiset" band
  // instead of inside any chapter.
  const activeReportType = typeTabRow === 'single' ? reportType : (typeFilter !== 'ALL' && typeFilter !== UNIVERSAL ? typeFilter : null);
  const chaptersByType = useReportChaptersMap(activeReportType ? [activeReportType] : []);
  const chapterDefs = activeReportType ? (chaptersByType[activeReportType] || []) : [];
  const tagsByChapter = chapterDefs.map(ch => ({
    ...ch,
    tags: visibleTags.filter(t => (t.sections || []).some(s => s.report_type === activeReportType && s.section_type === ch.key)),
  }));
  const universalTags = chapterDefs.length
    ? visibleTags.filter(t => !(t.sections || []).some(s => s.report_type === activeReportType))
    : visibleTags;
  const newTagReportTypeLabel = typeFilter === 'ALL' ? null : typeTabLabel(typeFilter);

  // `activeChapterState` only records an explicit user pick; re-derive the
  // chapter actually shown on every render so it stays valid as
  // chapterDefs loads in (async, via useReportChaptersMap) or the
  // report-type scope changes — falls back to the first chapter once
  // chapters are known, or "Yleiset" if there are none.
  const currentChapterKey = activeChapterState && (activeChapterState === UNIVERSAL || chapterDefs.some(c => c.key === activeChapterState))
    ? activeChapterState
    : (chapterDefs[0]?.key ?? UNIVERSAL);
  const currentChapterDef = chapterDefs.find(c => c.key === currentChapterKey);
  const currentChapterLabel = currentChapterKey === UNIVERSAL ? 'Yleiset' : (currentChapterDef?.label ?? '');
  const currentChapterTags = currentChapterKey === UNIVERSAL
    ? universalTags
    : (tagsByChapter.find(c => c.key === currentChapterKey)?.tags ?? []);

  return (
    <>
    <div className="d-flex" style={{ flexDirection: 'column' }}>
      {allReportTypes.length > 0 && (
        <div className="d-flex align-items-center gap-2" style={{
          padding: '10px 18px', position: 'relative',
          borderBottom: typeTabRow === 'all' ? '1px solid #dee2e6' : undefined,
          background: typeTabRow === 'all' ? '#fff' : undefined,
        }}>
          {typeTabRow === 'all' && <span style={{ fontSize: '0.78rem', color: '#6c757d', fontWeight: 650 }}>Raporttityyppi:</span>}
          <button type="button" onClick={() => setTypeDropdownOpen(o => !o)} aria-expanded={typeDropdownOpen}
            className="d-flex align-items-center gap-2"
            style={{
              fontSize: '0.84rem', fontWeight: 700, padding: '6px 14px', borderRadius: 999,
              border: '1px solid #dee2e6', background: '#fff', color: '#198754', cursor: 'pointer',
            }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: activeTypeOption?.dot }} />
            {activeTypeOption?.label}{activeTypeOption?.count != null ? ` (${activeTypeOption.count})` : ''}
            <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem', color: '#adb5bd' }} />
          </button>
          {typeDropdownOpen && (
            <>
              <div onClick={() => setTypeDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4, width: 260, maxHeight: 320, overflowY: 'auto',
                background: '#fff', border: '1px solid #e4e6e8', borderRadius: 10, boxShadow: '0 8px 24px rgba(20,20,22,0.12)',
                padding: 6, zIndex: 21,
              }}>
                {typeOptions.map(o => (
                  <button key={o.key} type="button"
                    onClick={() => { setTypeFilter(o.key); setTypeDropdownOpen(false); }}
                    className="d-flex align-items-center gap-2 text-start w-100"
                    style={{
                      border: 'none', background: typeFilter === o.key ? '#eafbf1' : 'none', borderRadius: 7,
                      padding: '7px 9px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                      color: typeFilter === o.key ? '#198754' : '#212529',
                    }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: o.dot }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                    {o.count != null && <span style={{ fontSize: '0.7rem', color: '#8e8e93' }}>{o.count}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="d-flex" style={{ borderTop: '1px solid #dee2e6' }}>
        {chapterDefs.length > 0 ? (
          <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid #dee2e6', padding: '10px 8px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#8e8e93', textTransform: 'uppercase', padding: '2px 9px 6px' }}>
              Kappaleet
            </div>
            {chapterDefs.map(ch => {
              const count = tagsByChapter.find(c => c.key === ch.key)?.tags.length ?? 0;
              const isActive = currentChapterKey === ch.key;
              return (
                <button key={ch.key} type="button" onClick={() => {
                  setActiveChapter(ch.key);
                  setSelectedTag(tagsByChapter.find(c => c.key === ch.key)?.tags[0]?.nimi ?? null);
                }}
                  className="d-flex align-items-center gap-2 text-start"
                  style={{
                    border: 'none', background: isActive ? '#eafbf1' : 'none', borderRadius: 7, marginBottom: 1,
                    padding: '7px 9px', fontSize: '0.84rem', fontWeight: 650, cursor: 'pointer',
                    color: isActive ? '#198754' : '#212529',
                  }}
                  onMouseEnter={isActive ? undefined : e => { e.currentTarget.style.background = '#f1f3f4'; }}
                  onMouseLeave={isActive ? undefined : e => { e.currentTarget.style.background = 'none'; }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.label}</span>
                  <span style={{
                    fontSize: '0.68rem', color: isActive ? '#fff' : '#8e8e93', background: isActive ? '#198754' : '#f1f3f4',
                    borderRadius: 999, padding: '1px 7px', flexShrink: 0,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
            <div style={{ borderTop: '1px solid #dee2e6', marginTop: 6, paddingTop: 6 }}>
              <button type="button" onClick={() => { setActiveChapter(UNIVERSAL); setSelectedTag(universalTags[0]?.nimi ?? null); }}
                className="d-flex align-items-center gap-2 text-start w-100"
                style={{
                  border: 'none', background: currentChapterKey === UNIVERSAL ? '#eafbf1' : 'none', borderRadius: 7,
                  padding: '7px 9px', fontSize: '0.84rem', fontWeight: 650, fontStyle: 'italic', cursor: 'pointer',
                  color: currentChapterKey === UNIVERSAL ? '#198754' : '#8e8e93',
                }}
                onMouseEnter={currentChapterKey === UNIVERSAL ? undefined : e => { e.currentTarget.style.background = '#f1f3f4'; }}
                onMouseLeave={currentChapterKey === UNIVERSAL ? undefined : e => { e.currentTarget.style.background = 'none'; }}>
                <span style={{ flex: 1 }}>Yleiset</span>
                <span style={{
                  fontSize: '0.68rem', color: currentChapterKey === UNIVERSAL ? '#fff' : '#8e8e93',
                  background: currentChapterKey === UNIVERSAL ? '#198754' : '#f1f3f4',
                  borderRadius: 999, padding: '1px 7px', flexShrink: 0,
                }}>
                  {universalTags.length}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid #dee2e6', padding: '10px 8px', display: 'flex', flexDirection: 'column' }}>
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Hae tageja…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ fontSize: '0.84rem' }}
            />
            {visibleTags.map(t => (
              <TagRow key={t.id} t={t} selectedTag={selectedTag} setSelectedTag={setSelectedTag}
                handleDeleteTag={handleDeleteTag} allReportTypes={allReportTypes} />
            ))}
            {visibleTags.length === 0 && (
              <div style={{ color: '#adb5bd', fontSize: '0.82rem', padding: '3px 4px' }}>
                {allTags.length === 0 ? 'Ei tageja vielä.' : 'Ei hakutuloksia.'}
              </div>
            )}
            <div style={{ borderTop: '1px solid #dee2e6', marginTop: 6, paddingTop: 6 }}>
              <button type="button" className="btn btn-sm btn-success w-100" style={{ fontSize: '0.8rem' }}
                onClick={() => setNewTagModal({ sectionKey: null, sectionLabel: null })}>
                + Uusi tagi
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {chapterDefs.length > 0 && (
            <div style={{ borderBottom: '1px solid #dee2e6', padding: '12px 16px' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#8e8e93', textTransform: 'uppercase' }}>
                  {currentChapterLabel} — tagit
                </span>
                <input
                  className="form-control form-control-sm"
                  placeholder="Hae tageja…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  style={{ fontSize: '0.8rem', width: 180 }}
                />
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {currentChapterTags.map(t => (
                  <TagChip key={t.id} t={t} selectedTag={selectedTag} setSelectedTag={setSelectedTag}
                    handleDeleteTag={handleDeleteTag} />
                ))}
                {currentChapterTags.length === 0 && (
                  <span style={{ fontSize: '0.8rem', color: '#adb5bd', fontStyle: 'italic' }}>
                    {query.trim() ? 'Ei hakutuloksia.' : 'Ei tageja tässä kappaleessa.'}
                  </span>
                )}
                <button type="button"
                  onClick={() => setNewTagModal({
                    sectionKey: currentChapterKey === UNIVERSAL ? null : currentChapterKey,
                    sectionLabel: currentChapterKey === UNIVERSAL ? null : currentChapterLabel,
                  })}
                  style={{ fontSize: '0.76rem', padding: '5px 12px', borderRadius: 999, border: '1px dashed #ced4da', background: '#fff', color: '#6c757d', cursor: 'pointer' }}>
                  + Uusi tagi
                </button>
              </div>
            </div>
          )}
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
            {!selectedTag && (
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Valitse tagi {chapterDefs.length > 0 ? 'yltä' : 'vasemmalta'}.
              </span>
            )}
            {selectedTagObj && allReportTypes.length > 0 && (
              <TagReportTypesPicker
                tag={selectedTagObj}
                allReportTypes={allReportTypes}
                onChange={types => onTagReportTypesChange?.(selectedTagObj.id, types)}
              />
            )}
            {selectedTagObj && (
              <ChapterScopePicker
                tag={selectedTagObj}
                onChange={sections => onTagSectionsChange?.(selectedTagObj.id, sections)}
              />
            )}
            {selectedTag && list.length === 0 && !addingNew && <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Ei valmislauseita.</span>}
            {list.map((t, i) => (
              <ValmislauseCard
                key={t.id}
                item={t}
                isFirst={i === 0}
                isLast={i === list.length - 1}
                rakennusData={rakennusData}
                mergedRakennusData={mergedRakennusData}
                onSave={patch => handleSaveItem(t, patch)}
                onDelete={() => handleDeleteValmislause(t)}
                onMoveUp={() => handleMove(t.id, -1)}
                onMoveDown={() => handleMove(t.id, 1)}
                onUse={handleUse}
              />
            ))}

            {selectedTag && !addingNew && (
              <button className="btn btn-sm" onClick={() => setAddingNew(true)}
                style={{ border: '1px dashed #adb5bd', background: '#fff', color: '#6c757d', borderRadius: 9, fontWeight: 600, fontSize: '0.88rem', padding: '8px 12px' }}>
                + Uusi valmislause
              </button>
            )}
          </div>

          {/* Its own fixed-height, independently scrolling area below the
              list — previously part of the same scrollable column as the
              existing valmislauseet, so a tall new-sentence form (title +
              rich text editor + condition builder) could squeeze them down
              to a sliver or off-screen entirely. */}
          {addingNew && (
            <div style={{ borderTop: '1px solid #dee2e6', padding: '14px 16px' }}>
              <div className="rounded border" style={{ borderColor: '#b8ddb8', padding: '10px 12px', background: '#fafffb' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#198754', marginBottom: 8 }}>
                  Uusi valmislause — {selectedTag}
                </div>

                {newHasCondition ? (
                  <div className="mb-2" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 7, padding: '9px 12px' }}>
                    <ConditionFields
                      param={newCondParam} op={newCondOp} val={newCondVal}
                      onParamChange={handleNewCondParamChange} onOpChange={setNewCondOp} onValChange={setNewCondVal}
                      rakennusData={rakennusData} mergedRakennusData={mergedRakennusData}
                    />
                    <button className="btn btn-sm btn-outline-danger mt-2" style={{ fontSize: '0.82rem', padding: '2px 9px' }}
                      onClick={() => { setNewHasCondition(false); setNewCondVal(''); }}>
                      Poista ehto
                    </button>
                  </div>
                ) : (
                  <button className="mb-2" onClick={() => setNewHasCondition(true)}
                    style={{
                      border: '1px dashed #adb5bd', background: '#fff', color: '#6c757d', cursor: 'pointer',
                      borderRadius: 999, padding: '2px 9px', fontSize: '0.78rem', fontWeight: 700,
                    }}>
                    + ehto
                  </button>
                )}

                <div className="mb-2">
                  <MinimalLexicalEditor
                    editorRef={newTekstiEditorRef}
                    value={newTeksti}
                    onChange={setNewTeksti}
                    placeholder="Lauseen teksti…"
                    minHeight={80}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-success" disabled={savingNew || !newTeksti.trim() || (newHasCondition && !newCondVal.trim())} onClick={handleCreateValmislause}
                    style={{ fontSize: '0.84rem' }}>
                    {savingNew ? 'Tallennetaan…' : 'Tallenna'}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={resetNewForm}
                    style={{ fontSize: '0.84rem' }}>
                    Peru
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedTag && onInsert && (
            // Sticky to the bottom of the nearest real scroll container —
            // the modal card itself now that it's the one scrollable
            // element — so the primary action stays reachable without
            // hunting for it, however long the content above grows.
            <div style={{ borderTop: '1px solid #dee2e6', padding: '10px 16px', flexShrink: 0, position: 'sticky', bottom: 0, background: '#fff' }}>
              <button className="btn btn-sm" onClick={handleUseTag}
                style={{ width: '100%', background: '#198754', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 650, fontSize: '0.9rem', padding: '8px 12px' }}>
                Käytä valmislausetta
              </button>
            </div>
          )}
        </div>

        <div style={{ width: 216, flexShrink: 0, borderLeft: '1px solid #dee2e6', padding: '14px 16px' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#6c757d', marginBottom: 2 }}>
            Muuttujat
          </div>
          {addingNew && (
            <div style={{ fontSize: '0.78rem', color: '#adb5bd', marginBottom: 6 }}>
              Klikkaa lisätäksesi tekstiin
            </div>
          )}
          {!addingNew && <div style={{ marginBottom: 8 }} />}
          {CONDITION_PARAMS.map(p => {
            const v = extractParamValue(p.key, rakennusData, mergedRakennusData);
            return (
              <div
                key={p.key}
                onClick={addingNew ? () => insertMuuttuja(p.key) : undefined}
                title={addingNew ? `Lisää {{${p.key}}} tekstiin` : undefined}
                className="d-flex justify-content-between"
                style={{
                  fontSize: '0.84rem', padding: '5px 6px', margin: '0 -6px', borderRadius: 6,
                  borderBottom: '1px solid #f1f3f4', cursor: addingNew ? 'pointer' : 'default',
                  transition: 'background-color .1s',
                }}
                onMouseEnter={addingNew ? e => { e.currentTarget.style.background = '#eafbf1'; } : undefined}
                onMouseLeave={addingNew ? e => { e.currentTarget.style.background = 'transparent'; } : undefined}
              >
                <span style={{ color: '#6c757d' }}>{p.label}</span>
                <span style={{ fontWeight: 650 }}>{v != null ? v : '—'}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    {newTagModal && (
      <NewTagModal
        reportTypeLabel={newTagReportTypeLabel}
        sectionLabel={newTagModal.sectionLabel}
        onCreate={handleCreateTagModal}
        onClose={() => setNewTagModal(null)}
      />
    )}
    </>
  );
}
